// Client-side hints for choosing a password. The server's policy (server/src/passwords.ts) is the
// authority – it also refuses common passwords and words – and its message is shown when it says no.

import { plural } from './format';

export const MIN_PASSWORD_LENGTH = 12;

/** Login names: lowercase letters, digits, dot, dash, underscore; 3–40 characters (shared/types.ts). */
export const USERNAME_PATTERN = /^[a-z0-9._-]{3,40}$/;

export const USERNAME_HINT = 'Male črke brez šumnikov, številke, pika, vezaj ali podčrtaj; 3–40 znakov (npr. ana.novak).';

export interface PasswordHint {
  /** 0 = not acceptable, 1 = acceptable, 2 = good, 3 = strong. */
  level: 0 | 1 | 2 | 3;
  text: string;
}

const codePoints = (s: string) => [...s].length;

/** A short Slovenian assessment of `password` as the user types it. */
export function passwordHint(password: string, username = ''): PasswordHint {
  const length = codePoints(password);
  if (length === 0) return { level: 0, text: `Najmanj ${MIN_PASSWORD_LENGTH} znakov. Najlažje si zapomnite stavek iz več besed.` };
  if (length < MIN_PASSWORD_LENGTH) {
    const missing = MIN_PASSWORD_LENGTH - length;
    return { level: 0, text: `Prekratko – manjka še ${plural(missing, ['znak', 'znaka', 'znaki', 'znakov'])}.` };
  }
  const lower = password.toLocaleLowerCase('sl');
  const name = username.trim().toLocaleLowerCase('sl');
  // Like the server: not the user name, also not followed only by digits or symbols ("ana2026!").
  if (name && lower.startsWith(name) && /^[^\p{L}]*$/u.test(lower.slice(name.length))) {
    return { level: 0, text: 'Geslo ne sme biti uporabniško ime.' };
  }
  if (/^\d+$/.test(password)) return { level: 0, text: 'Same številke niso dovolj varne.' };
  if (new Set(lower).size < 5) return { level: 0, text: 'Preveč ponavljajočih se znakov.' };
  let classes = 0;
  if (/[a-zčšžćđ]/.test(password)) classes++;
  if (/[A-ZČŠŽĆĐ]/.test(password)) classes++;
  if (/\d/.test(password)) classes++;
  if (/[^\p{L}\p{N}]/u.test(password)) classes++;
  if (length >= 20 || (length >= 16 && classes >= 3)) return { level: 3, text: 'Močno geslo.' };
  if (length >= 15 || classes >= 3) return { level: 2, text: 'Dobro geslo.' };
  return { level: 1, text: 'Sprejemljivo geslo – daljše bi bilo varnejše.' };
}

/** Normalised login name as the server stores it (case-insensitive). */
export function normalizeUsername(input: string): string {
  return input.trim().toLowerCase();
}
